import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Briefcase, Users, GraduationCap, Heart, Sun, TrendingUp,
  Loader2, ArrowRight, Check, X,
} from "lucide-react";
import { authFetch } from "@/lib/authFetch";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const ICONS = {
  Briefcase, Users, GraduationCap, Heart, Sun, TrendingUp,
};

export default function ProfileSelection({ onApplied, onSkip, onApply }) {
  const [profiles, setProfiles] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [isApplying, setIsApplying] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await authFetch(`${API_URL}/api/onboarding/profiles`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const body = await r.json();
        if (!cancelled) setProfiles(body.profiles || []);
      } catch (err) {
        if (!cancelled) setError(String(err?.message || err));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  async function handleApply() {
    if (!selectedKey || isApplying) return;
    setIsApplying(true);
    try {
      const data = await onApply(selectedKey);
      onApplied?.(data);
    } catch (err) {
      setError(String(err?.message || err));
    } finally {
      setIsApplying(false);
    }
  }

  return (
    <div data-testid="profile-selection">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-white mb-2">
          Escolha seu perfil
        </h2>
        <p className="text-[#94A3B8] text-sm max-w-md mx-auto">
          Crio categorias, hábitos, lembretes e projetos relevantes em segundos.
        </p>
      </div>

      {error && (
        <div className="text-red-400 text-xs mb-4 text-center">{error}</div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
        {profiles.map((profile) => {
          const Icon = ICONS[profile.icon] || Briefcase;
          const isSelected = selectedKey === profile.key;
          return (
            <motion.button
              key={profile.key}
              type="button"
              data-testid={`profile-card-${profile.key}`}
              onClick={() => setSelectedKey(profile.key)}
              whileHover={{ y: -1 }}
              whileTap={{ scale: 0.99 }}
              className="text-left p-4 rounded-xl border transition-all"
              style={{
                background: isSelected ? "rgba(99, 102, 241, 0.1)" : "rgba(18, 24, 38, 0.6)",
                borderColor: isSelected ? "#6366F1" : "#1F2A44",
              }}
            >
              <div className="flex items-start gap-3">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{
                    background: isSelected ? "rgba(99, 102, 241, 0.2)" : "rgba(99, 102, 241, 0.08)",
                  }}
                >
                  <Icon
                    className="w-5 h-5"
                    style={{ color: isSelected ? "#A5B4FC" : "#6366F1" }}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-semibold mb-0.5">
                    {profile.name}
                  </p>
                  <p className="text-[#64748B] text-xs mb-2 leading-snug">
                    {profile.description}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    <Pill>{profile.counts.categories} categorias</Pill>
                    <Pill>{profile.counts.habits} hábitos</Pill>
                    <Pill>{profile.counts.reminders} lembretes</Pill>
                    <Pill>{profile.counts.projects} projetos</Pill>
                  </div>
                </div>
                <div
                  className="w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0"
                  style={{
                    borderColor: isSelected ? "#6366F1" : "#475569",
                    background: isSelected ? "#6366F1" : "transparent",
                  }}
                >
                  {isSelected && <Check className="w-3 h-3 text-white" />}
                </div>
              </div>
            </motion.button>
          );
        })}
      </div>

      <div className="flex gap-3">
        <button
          type="button"
          data-testid="profile-skip"
          onClick={onSkip}
          disabled={isApplying}
          className="flex-1 py-3 rounded-xl text-[#94A3B8] text-sm font-medium border transition-all hover:text-white hover:border-[#475569] disabled:opacity-40"
          style={{ borderColor: "#1F2A44" }}
        >
          <span className="inline-flex items-center gap-1.5">
            <X className="w-4 h-4" />
            Configurar manualmente
          </span>
        </button>
        <button
          type="button"
          data-testid="profile-apply"
          onClick={handleApply}
          disabled={!selectedKey || isApplying}
          className="flex-1 py-3 rounded-xl text-white text-sm font-semibold transition-all hover:opacity-90 disabled:opacity-40 inline-flex items-center justify-center gap-2"
          style={{ background: "linear-gradient(135deg, #6366F1, #8B5CF6)" }}
        >
          {isApplying ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              Aplicar perfil
              <ArrowRight className="w-4 h-4" />
            </>
          )}
        </button>
      </div>
    </div>
  );
}

function Pill({ children }) {
  return (
    <span
      className="text-[10px] px-1.5 py-0.5 rounded text-[#94A3B8]"
      style={{ background: "rgba(99, 102, 241, 0.08)" }}
    >
      {children}
    </span>
  );
}
